/**
 * По-полевая таблица посевного плана (макет v2-15, утв. founder 19.08).
 *
 * Одна строка — одно поле, и это осознанно другая нарезка, чем у соседнего
 * экрана назначения техкарт (там строка — «культура по предшественнику»).
 * Технологию назначают на группу полей, а решение «что тут посеять» агроном
 * принимает глядя на конкретное поле: его площадь, его историю. Поэтому здесь
 * поле — подлежащее.
 *
 * История показана ПОЛОСОЙ ЛЕТ (AK-767): колонка на каждый сезон глубины, а
 * два последних года — препредшественник и предшественник — выделены отдельно.
 * Пары лет мало: возврат культуры через год по ней виден, а возврат раз в 6-7
 * лет (норматив подсолнечника и свёклы) — нет, и решение агронома опиралось бы
 * на половину картины. Предшественник при этом подсвечен: полосу читают целиком,
 * но решение «что сеять» отталкивается прежде всего от прошлого года.
 *
 * Сорт и урожайность правятся ПРЯМО В СТРОКЕ: это уточнение уже принятого
 * решения, ради которого открывать диалог — лишний шаг на каждое из двух сотен
 * полей.
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { originBadge } from '@/lib/planning/autoBuild';
import {
  useCropVarieties,
  type FieldHistoryYear,
  type VariantField,
} from '@/lib/planning/planCrops';
import { formatHa } from '@/lib/planning/planVariant';
import {
  cropCellText,
  fieldPlanStatus,
  isRowEditable,
  type FieldPlanStatus,
} from './sowingPlan';

const YIELD = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

/** Radix Select не принимает пустую строку как значение — нужен свой маркер. */
const NO_VARIETY = '__none__';

const STATUS_LABEL: Record<FieldPlanStatus, string> = {
  assigned: 'культура назначена',
  fallow: 'поле отдыхает',
  unassigned: 'культура не назначена',
  by_subzones: 'засеяны зоны поля',
};

/** Подпись состояния строки словами агронома. */
export function statusText(status: FieldPlanStatus): string {
  return STATUS_LABEL[status];
}

/**
 * Ячейка истории поля — прошлый или позапрошлый факт.
 *
 * Пустая ячейка при праве `sowings:C` кликабельна: «нет данных · указать»
 * открывает ручной ввод факта того сезона (AK-694). Без права — прежний
 * тихий текст: упереться в 403 на последнем шаге хуже, чем не увидеть кнопку.
 * Без года колонки (у компании нет такого сезона) записывать некуда — кнопки
 * тоже нет.
 */
function HistoryCell({
  name,
  fieldName,
  seasonYear,
  canFill,
  onFill,
}: {
  name: string | null;
  fieldName: string;
  seasonYear: number | null;
  canFill: boolean;
  onFill?: () => void;
}) {
  if (name) {
    return (
      <Badge variant="secondary" className="font-normal">
        {name}
      </Badge>
    );
  }
  if (!canFill || seasonYear === null || !onFill) {
    return <span className="text-xs text-muted-foreground">нет данных</span>;
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="h-8 px-2 text-xs font-normal text-muted-foreground"
      aria-label={`Указать, что росло на поле ${fieldName} в сезоне ${seasonYear}`}
      onClick={onFill}
    >
      нет данных · <span className="text-accent-brand">указать</span>
    </Button>
  );
}

/**
 * Ячейка года в полосе истории (AK-767).
 *
 * Полное название культуры, а не сокращение. Соблазн ужать «Озимая пшеница» до
 * «оз.пш» велик — семь колонок места просят, — но сокращения в интерфейсе
 * агронома обязаны быть ЕГО, а не выдуманными нами: свой набор аббревиатур
 * есть в каждом хозяйстве, и наш совпал бы не с одним. Поэтому имя обрезается
 * шириной ячейки и целиком показывается по наведению.
 *
 * Пустой год — тире, а не пустая ячейка: пустое место в таблице читается как
 * «сломалось», а тире — как «данных нет».
 */
function YearCell({ entry }: { entry: FieldHistoryYear | undefined }) {
  if (!entry || !entry.name) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span
      title={entry.name}
      className={cn(
        'block max-w-[80px] truncate rounded-sm px-1 py-1 text-xs',
        entry.is_fallow ? 'bg-muted text-muted-foreground' : 'bg-secondary text-secondary-foreground',
      )}
    >
      {entry.name}
    </span>
  );
}

/**
 * Откуда взялась цифра урожайности (Р2).
 *
 * Метка нужна ровно затем, чтобы правка норматива не выглядела как правка
 * плана: «наследовано» означает «поедет за нормативом при следующем заведении»,
 * «переопределено» — «на этом поле решили иначе, норматив его не касается».
 * Без метки агроном не может отличить одно от другого, и любая правка
 * норматива превращается в обход двухсот полей глазами.
 */
function YieldSourceTag({ field }: { field: VariantField }) {
  if (!field.plan_crop_id) {
    // Пар — состояние поля, а не культура: урожайности у него нет и быть не
    // может (Р1). Говорим это словами, а не прочерком, который читается как
    // «забыли заполнить».
    return field.occupied_by ? (
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">не применимо</span>
    ) : null;
  }
  if (field.yield_source === 'inherited') {
    return (
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">наследов.</span>
    );
  }
  if (field.yield_source === 'overridden') {
    return (
      <span
        className="text-[10px] uppercase tracking-wide text-accent-brand"
        title={
          field.yield_default_c_ha !== null
            ? `Норматив хозяйства — ${YIELD.format(field.yield_default_c_ha)} ц/га`
            : undefined
        }
      >
        переопр.
      </span>
    );
  }
  return null;
}

/**
 * Сорт строки. Список сортов спрашивается ПО КУЛЬТУРЕ поля, поэтому запрос
 * живёт в самой ячейке и уходит только когда её раскрыли: тянуть справочник
 * сортов на две сотни строк разом — это две сотни запросов ни за чем.
 */
function VarietyCell({
  field,
  canEdit,
  onPick,
  pending,
}: {
  field: VariantField;
  canEdit: boolean;
  onPick: (varietyId: string | null) => void;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const varieties = useCropVarieties(open ? field.plan_crop_id : null);

  if (!field.plan_crop_id) {
    // Сорт без культуры не выбрать: справочник сортов ведётся ПО культуре.
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (!canEdit) {
    return field.plan_variety_name ? (
      <span className="text-sm">{field.plan_variety_name}</span>
    ) : (
      <span className="text-xs text-muted-foreground">не выбран</span>
    );
  }
  if (!open) {
    return (
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-8 px-2 font-normal"
        disabled={pending}
        onClick={() => setOpen(true)}
      >
        {field.plan_variety_name ?? <span className="text-muted-foreground">выбрать сорт</span>}
      </Button>
    );
  }
  if (varieties.isLoading) {
    // Select монтируем только с готовым списком: `defaultOpen` на
    // заблокированном селекте не раскрылся бы, и первый клик пропал бы зря.
    return (
      <Button type="button" size="sm" variant="ghost" className="h-8 px-2 font-normal" disabled>
        загружаем сорта…
      </Button>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <Select
        value={field.plan_variety_id ?? NO_VARIETY}
        disabled={pending}
        // Кнопку «выбрать сорт» уже нажали — список раскрываем сразу, второй
        // клик по той же ячейке был бы платой ни за что (двести строк — двести
        // лишних кликов). Закрыли список без выбора — возвращаемся к кнопке.
        defaultOpen
        onOpenChange={(o) => {
          if (!o) setOpen(false);
        }}
        onValueChange={(v) => {
          onPick(v === NO_VARIETY ? null : v);
          setOpen(false);
        }}
      >
        {/* aria-label на триггере: у выпадашки в строке нет видимой подписи —
            колонка одна на две сотни строк, и «Сорт» без имени поля не
            сказал бы, о каком поле речь. */}
        <SelectTrigger className="h-8 w-40" aria-label={`Сорт на поле ${field.name}`}>
          <SelectValue placeholder="выбрать сорт" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_VARIETY}>— без сорта —</SelectItem>
          {(varieties.data ?? []).map((v) => (
            <SelectItem key={v.id} value={v.id}>
              {v.name}
              {v.is_hybrid ? ' (гибрид)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Ошибка ≠ пустота: «нет сортов» при упавшем запросе — враньё, из-за
          которого сорта перестали бы искать в справочнике. */}
      {varieties.isError && (
        <span className="text-xs text-destructive">
          не удалось загрузить сорта — попробуйте ещё раз
        </span>
      )}
      {!varieties.isError && (varieties.data ?? []).length === 0 && (
        <span className="text-xs text-muted-foreground">
          в справочнике нет сортов этой культуры
        </span>
      )}
    </div>
  );
}

/**
 * Урожайность строки. Правку отправляем по уходу из поля (blur), а не на каждое
 * нажатие: посимвольный PATCH превратил бы ввод «52» в три запроса, а строку —
 * в мигающую.
 */
function YieldCell({
  field,
  canEdit,
  onCommit,
  pending,
}: {
  field: VariantField;
  canEdit: boolean;
  onCommit: (value: number | null) => void;
  pending: boolean;
}) {
  const initial = field.planned_yield_c_ha === null ? '' : String(field.planned_yield_c_ha);
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  // Значение могло приехать с сервера (смена культуры, чужая правка) — черновик
  // обязан за ним следовать, иначе поле показывает то, чего в плане уже нет.
  useEffect(() => {
    setDraft(initial);
    setError(null);
  }, [initial]);

  if (!field.plan_crop_id) return <span className="text-xs text-muted-foreground">—</span>;
  if (!canEdit) {
    return (
      <span className="tabular-nums">
        {field.planned_yield_c_ha === null ? '—' : YIELD.format(field.planned_yield_c_ha)}
      </span>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === initial.trim()) {
      setError(null);
      return;
    }
    if (trimmed === '') {
      setError(null);
      onCommit(null);
      return;
    }
    const parsed = Number(trimmed.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed < 0) {
      // Набранное НЕ затираем: человек видит, что именно он ввёл, и правит.
      // Молчаливый откат к прежней цифре выглядел бы как «система съела ввод».
      setError('Урожайность — число не меньше нуля, например 52 или 48,5');
      return;
    }
    setError(null);
    onCommit(parsed);
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Input
        inputMode="decimal"
        className={cn(
          'h-8 w-24 text-right tabular-nums',
          error && 'border-destructive focus-visible:ring-destructive',
        )}
        aria-label={`Плановая урожайность на поле ${field.name}, ц/га`}
        aria-invalid={error ? true : undefined}
        value={draft}
        disabled={pending}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(null);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setDraft(initial);
            setError(null);
          }
        }}
      />
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

export function FieldRowsTable({
  fields,
  selected,
  onToggle,
  onToggleAll,
  canAssign,
  canClear,
  canTune,
  onAssign,
  onClear,
  onPickVariety,
  onCommitYield,
  onFillHistory,
  pendingIds,
}: {
  fields: VariantField[];
  selected: ReadonlySet<string>;
  onToggle: (field: VariantField) => void;
  onToggleAll: (checked: boolean) => void;
  /**
   * Права по-строчно, а не одним «можно править»: назначение — `sowings:C`,
   * снятие — `sowings:D`, сорт и урожайность — `sowings:U`. Кнопка, на которую
   * права нет, не показывается вовсе — упереться в 403 на последнем шаге хуже,
   * чем не увидеть кнопку (гейт прав ставится на входе во флоу).
   */
  canAssign: boolean;
  canClear: boolean;
  canTune: boolean;
  onAssign: (field: VariantField) => void;
  onClear: (field: VariantField) => void;
  onPickVariety: (field: VariantField, varietyId: string | null) => void;
  onCommitYield: (field: VariantField, value: number | null) => void;
  /**
   * Клик по пустой ячейке истории: ручной факт прошлого сезона (AK-694).
   * Запись факта — то же право `sowings:C`, что и назначение культуры,
   * поэтому отдельного can-флага нет: гейт — `canAssign`.
   */
  onFillHistory: (field: VariantField, which: 'predecessor' | 'predecessor2') => void;
  /** Поля, по которым прямо сейчас идёт запись — вся партия массового снятия. */
  pendingIds: ReadonlySet<string>;
}) {
  // Чекбоксы кормят оба массовых действия — назначение и снятие.
  const canSelect = canAssign || canClear;
  const selectable = fields.filter(isRowEditable);
  const allSelected = selectable.length > 0 && selectable.every((f) => selected.has(f.id));
  // Годы одинаковы у всех строк — берём с первой попавшейся.
  const prevYear = fields.find((f) => f.predecessor_year !== null)?.predecessor_year ?? null;
  const prev2Year = fields.find((f) => f.predecessor2_year !== null)?.predecessor2_year ?? null;
  // Полоса лет (AK-767). Годы одинаковы у всех строк — сервер шлёт по записи на
  // каждый сезон глубины, в том числе пустой, ровно чтобы колонки не разъезжались.
  const years = fields.find((f) => f.history.length > 0)?.history.map((h) => h.year) ?? [];
  // Последний год полосы — предшественник, предпоследний — препредшественник:
  // это ТЕ ЖЕ сезоны, из которых считаются `predecessor_*`, и подписывать их
  // отдельно, «годом минус один», значило бы завести второй источник правды.
  const olderYears = years.slice(0, -2);
  const historyByYear = (field: VariantField) => new Map(field.history.map((h) => [h.year, h]));

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {canSelect && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                disabled={selectable.length === 0}
                onCheckedChange={(v) => onToggleAll(v === true)}
                aria-label="Выбрать все показанные поля"
              />
            </TableHead>
          )}
          <TableHead>Поле</TableHead>
          <TableHead className="text-right">Га</TableHead>
          {/* Ранние годы полосы — только чтение: править историю пятилетней
              давности из планового экрана незачем, для этого есть карточка
              поля. Кликабельны две последние колонки, по которым и считается
              севооборот. */}
          {olderYears.map((year) => (
            <TableHead key={year} className="text-center text-xs">
              {year}
            </TableHead>
          ))}
          {/* Год не вычисляем как «минус два»: прошлый сезон — это предыдущий
              сезон КОМПАНИИ, и при пропущенном годе подпись бы врала. */}
          <TableHead>
            Препредшественник
            {prev2Year && (
              <span className="block text-xs font-normal text-muted-foreground">
                факт {prev2Year}
              </span>
            )}
          </TableHead>
          {/* Предшественник выделен фоном: вся полоса лет нужна, чтобы увидеть
              возврат культуры, но решение «что сеять» агроном принимает прежде
              всего от прошлого года, и он обязан читаться с одного взгляда. */}
          <TableHead className="bg-accent-brand/5">
            Предшественник
            {prevYear && (
              <span className="block text-xs font-normal text-muted-foreground">
                факт {prevYear}
              </span>
            )}
          </TableHead>
          <TableHead>Культура плановая</TableHead>
          <TableHead>Гибрид / сорт</TableHead>
          <TableHead className="text-right">Урожайность, ц/га</TableHead>
          {canSelect && <TableHead className="w-24" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {fields.map((field) => {
          const status = fieldPlanStatus(field);
          const editable = isRowEditable(field);
          const pending = pendingIds.has(field.id);
          return (
            <TableRow key={field.id} className={cn(!editable && 'bg-muted/30')}>
              {canSelect && (
                <TableCell>
                  <Checkbox
                    checked={selected.has(field.id)}
                    disabled={!editable}
                    onCheckedChange={() => onToggle(field)}
                    aria-label={`Поле ${field.name}`}
                  />
                </TableCell>
              )}
              <TableCell className="font-medium">
                {/* Имя поля — вход в его карточку (founder 24.08). Из старой
                    системы приезжают кривые поля, и «что это вообще за поле»
                    проверяется глядя в карточку — но НЕ ценой потери таблицы
                    плана: отсюда новая вкладка. Ссылка подчёркнута, а не
                    только подкрашена: что сюда можно нажать, должно быть видно
                    без наведения мыши. Выделения строк это не касается —
                    чекбокс остаётся единственным, чем строка выбирается. */}
                <Link
                  to="/fields/$fieldId"
                  params={{ fieldId: field.id }}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Открыть карточку поля ${field.name} в новой вкладке`}
                  className={cn(
                    'rounded-sm text-primary underline underline-offset-2',
                    'hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2',
                    'focus-visible:ring-ring focus-visible:ring-offset-2',
                    'focus-visible:ring-offset-background',
                  )}
                >
                  {field.name}
                </Link>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatHa(field.area_ha)}
              </TableCell>
              {olderYears.map((year) => (
                <TableCell key={year} className="text-center">
                  <YearCell entry={historyByYear(field).get(year)} />
                </TableCell>
              ))}
              <TableCell>
                <HistoryCell
                  name={field.predecessor2_name}
                  fieldName={field.name}
                  seasonYear={field.predecessor2_year}
                  canFill={canAssign}
                  onFill={() => onFillHistory(field, 'predecessor2')}
                />
              </TableCell>
              <TableCell className="bg-accent-brand/5">
                <HistoryCell
                  name={field.predecessor_name}
                  fieldName={field.name}
                  seasonYear={field.predecessor_year}
                  canFill={canAssign}
                  onFill={() => onFillHistory(field, 'predecessor')}
                />
              </TableCell>
              <TableCell>
                <div className="flex flex-col items-start gap-1">
                  {editable && canAssign ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className={cn(
                        'h-8 px-2 font-normal',
                        status === 'unassigned' && 'text-accent-brand',
                        // Рамка «поставлено правилом» — на кнопке, а не на
                        // ячейке: правило можно переиграть рукой прямо тут, и
                        // граница обязана совпадать с тем, на что нажимают.
                        field.rotation_rule_id && 'border border-primary/40 bg-primary/5',
                      )}
                      disabled={pending}
                      onClick={() => onAssign(field)}
                    >
                      {cropCellText(field)}
                    </Button>
                  ) : (
                    <span
                      className={cn(
                        'text-sm',
                        status === 'assigned' && 'font-medium text-foreground',
                        status === 'unassigned' && 'text-accent-brand',
                        (status === 'fallow' || status === 'by_subzones') &&
                          'text-muted-foreground',
                      )}
                    >
                      {cropCellText(field)}
                    </span>
                  )}
                  {/* Происхождение строки — подписью, а не только рамкой: цвет
                      один не отличит «правило» от «выделено», а на дальтонике
                      не сработает вовсе. Пометку показываем только у занятого
                      поля: у пустого происхождению неоткуда взяться. */}
                  {(() => {
                    // Пусто ТОЛЬКО у незанятого поля: занятой строке сервер
                    // отдаёт происхождение всегда (неизвестное — как ручное).
                    const badge = originBadge(field.plan_row_origin);
                    return badge ? (
                      <Badge variant={badge.variant} className="ml-2 shrink-0">
                        {badge.label}
                      </Badge>
                    ) : null;
                  })()}
                </div>
              </TableCell>
              <TableCell>
                <VarietyCell
                  field={field}
                  canEdit={canTune && editable}
                  pending={pending}
                  onPick={(varietyId) => onPickVariety(field, varietyId)}
                />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <YieldCell
                    field={field}
                    canEdit={canTune && editable}
                    pending={pending}
                    onCommit={(value) => onCommitYield(field, value)}
                  />
                  <YieldSourceTag field={field} />
                </div>
              </TableCell>
              {canSelect && (
                <TableCell>
                  {editable ? (
                    canClear &&
                    status !== 'unassigned' && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={pending}
                        onClick={() => onClear(field)}
                      >
                        Снять
                      </Button>
                    )
                  ) : (
                    // Зоны правятся в карточке поля (ADR-049). Пустая ячейка
                    // читалась бы как «кнопки не прогрузились».
                    <span className="text-xs text-muted-foreground">
                      правится в карточке поля
                    </span>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
